import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Send, Mail, MapPin, Phone, Clock, Sparkles, Check, Loader2, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const Section = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const ContactPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Failed to send');
      
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (err) {
      setError(t('contact.error') || 'Failed to send message');
    }
    setLoading(false);
  };

  const contactInfo = [
    { icon: Mail, label: 'Email', value: 'info@pirone.io', href: 'mailto:info@pirone.io', primary: true },
    { icon: MapPin, label: t('contact.location') || 'Location', value: 'New Jersey, USA' },
    { icon: Clock, label: t('contact.response') || 'Response', value: t('contact.responseTime') || '24 hours' }
  ];

  if (success) {
    return (
      <div className="min-h-screen bg-transparent pt-32 pb-24 flex items-center justify-center">
        {/* Background */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px]"
            style={{ background: 'radial-gradient(ellipse at center top, rgba(56, 189, 248, 0.06) 0%, transparent 60%)' }}
          />
        </div>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 text-center max-w-md mx-auto px-6"
        >
          <div className="w-24 h-24 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-8">
            <Check size={48} className="text-emerald-400" />
          </div>
          <h2 className="text-4xl font-semibold text-white mb-4">{t('contact.thankYou')}</h2>
          <p className="text-white/50 text-lg mb-8">{t('contact.thankYouText')}</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/10 text-white font-medium rounded-full hover:bg-white/10 transition-all"
          >
            <span>{t('common.backToHome') || 'Back to Home'}</span>
            <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent pt-32 pb-24">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px]"
          style={{ background: 'radial-gradient(ellipse at center top, rgba(56, 189, 248, 0.06) 0%, transparent 60%)' }}
        />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        <Section>
          {/* Header */}
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#38BDF8]/10 border border-[#38BDF8]/20 mb-6">
              <Sparkles size={14} className="text-[#38BDF8]" />
              <span className="text-sm font-medium text-[#38BDF8]">{t('contact.badge') || t('common.contact')}</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-semibold text-white mb-4">{t('contact.title')}</h1>
            <p className="text-lg text-white/50 max-w-[500px] mx-auto">{t('contact.subtitle')}</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div variants={fadeInUp}>
              <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/[0.06]">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-white/60 mb-2">{t('contact.nameLabel')}</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full h-12 px-4 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50 transition-colors"
                        placeholder={t('contact.namePlaceholder') || ''}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/60 mb-2">{t('contact.emailLabel')}</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full h-12 px-4 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50 transition-colors"
                        placeholder={t('contact.emailPlaceholder') || ''}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-white/60 mb-2">{t('contact.phoneLabel')}</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full h-12 px-4 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50 transition-colors"
                        placeholder={t('contact.phonePlaceholder') || ''}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/60 mb-2">{t('contact.companyLabel')}</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full h-12 px-4 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50 transition-colors"
                        placeholder={t('contact.companyPlaceholder') || ''}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-white/60 mb-2">{t('contact.messageLabel')}</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#38BDF8]/50 transition-colors resize-none"
                      placeholder={t('contact.messagePlaceholder') || ''}
                    />
                  </div>

                  {error && (
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-14 bg-[#38BDF8] hover:bg-white text-black font-semibold rounded-xl flex items-center justify-center gap-3 transition-all duration-300 disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 size={20} className="animate-spin" />
                    ) : (
                      <>
                        <Send size={18} />
                        {t('contact.submitButton')}
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div variants={fadeInUp} className="space-y-6">
              {contactInfo.map((item, i) => (
                <a
                  key={i}
                  href={item.href}
                  target={item.href?.startsWith('http') ? '_blank' : undefined}
                  rel={item.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className={`flex items-center gap-5 p-5 rounded-2xl bg-white/[0.02] border transition-all duration-300 group ${
                    item.primary 
                      ? 'border-[#38BDF8]/20 hover:border-[#38BDF8]/40' 
                      : 'border-white/[0.06] hover:border-white/[0.1]'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform ${
                    item.primary 
                      ? 'bg-[#38BDF8]/10 border border-[#38BDF8]/20' 
                      : 'bg-white/[0.03] border border-white/[0.06]'
                  }`}>
                    <item.icon size={20} className={item.primary ? 'text-[#38BDF8]' : 'text-white/50'} />
                  </div>
                  <div>
                    <div className="text-white/40 text-sm mb-1">{item.label}</div>
                    <div className={`font-medium ${item.primary ? 'text-[#38BDF8]' : 'text-white'}`}>{item.value}</div>
                  </div>
                </a>
              ))}

              {/* WhatsApp - Prominent CTA */}
              <a
                href="https://wa.me/15513548697"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 p-5 rounded-2xl bg-[#25D366]/10 border-2 border-[#25D366]/40 hover:bg-[#25D366]/20 hover:border-[#25D366] transition-all duration-300 hover:scale-[1.02]"
                data-testid="contact-whatsapp-btn"
              >
                <div className="w-14 h-14 rounded-xl bg-[#25D366] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#25D366]/30 group-hover:scale-110 transition-transform">
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" className="text-white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div>
                  <div className="text-white font-semibold text-lg">WhatsApp</div>
                  <div className="text-[#25D366] text-sm font-medium">{t('contact.whatsappCta') || 'Message us instantly'}</div>
                </div>
              </a>
            </motion.div>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default ContactPage;
